// eslint-disable-next-line import/no-named-as-default
import Icon from "@mdi/react";
import * as Sentry from "@sentry/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { QueryFunctionContext, useQuery } from "react-query";
import { useDispatch, useSelector } from "react-redux";

import AllInboxIcon from "@material-ui/icons/AllInbox";
import SwapVertIcon from "@material-ui/icons/SwapVert";
import {
  mdiBullseye,
  mdiCalendarRange,
  mdiChevronDown,
  mdiEye,
  mdiEyeOff,
  mdiSort,
  mdiSortAscending,
  mdiSortDescending
} from "@mdi/js";
import { Button, Popover, Switch, Tooltip, Typography } from "antd";
import { widgetHoverAtom } from "atoms/interaction";
import axios from "axios";
import classnames from "classnames";
import {
  ALL_PRODUCT_TYPES,
  AXIS_TYPE,
  IS_STACKED_BAR_CHART,
  IS_STACKED_BAR_HORIZONTAL_CHART
} from "constants/chart.constants";
import { UNDEFINED_LOCKS } from "entities/charts/chart";
import {
  getSplitYearUnit,
  isSplitYearFieldMismatch,
  isSplitYearResponseFresh
} from "entities/charts/eva/translators/splitYear";
import { useAtomValue } from "jotai";
import styled from "styled-components";
import { getProductType, stringToEnum } from "utils";
import formatPropertyName from "utils/column/formatPropertyName";
import getGroupByListColumnByProperty from "utils/column/getGroupByListColumnByProperty";
import { canHideOthers } from "utils/groupBy/canHideOthers";
import { getLinkToAxisLabel } from "utils/groupBy/maxBinCriteria";
import {
  findCatalogColumn,
  getMaxBinDefault,
  withMaxBinDefault
} from "utils/groupBy/maxBinDefaults";
import { supportsMaxBins } from "utils/groupBy/supportsMaxBins";

import { ALL_RESCATS } from "api/userForecasts";

import { mBinSize } from "models/binSize";
import { IProductType } from "models/chart";
import { EntityKind } from "models/entityKind";

import { useNoviAccess } from "components/arps/hooks/useHasNoviAccess";
import { Tooltip as EvaTooltip } from "components/base";
import BinSettings from "components/filter/BinSettings";
import { BinIconButton } from "components/filter/BinSettings.styles";
import { getStoredBinsFromSession } from "components/filter/util/getStoredBinsFromSession";
import GroupBy, { GroupByValueT } from "components/groupBy";
import { DEFAULT_RESCAT } from "components/groupBy/groupByStore";
import { getForecastSourceFromSelection } from "components/groupBy/helpers";
import {
  getForecastPropertySuffix,
  isForecastQualifiedProperty,
  isStaleForecastQualification,
  repairOrphanedForecastProperty,
  requalifyForecastProperty,
  splitForecastQualification
} from "components/groupBy/helpers/qualifyForecastProperty";

import {
  FORECAST_KEY,
  FORECAST_TRUMPING_FOLDER_ID,
  IS_BOX_PLOT_CHART,
  IS_CROSS_PLOT_CHART,
  IS_MOSAIC_CHART,
  IS_TREND_DATE_CHART,
  NOVI_FORECAST_SOURCE,
  WORKING_FORECAST_SOURCE,
  isForecastTrumpingSource
} from "../../../constants";
import { CalculationMethod, EvaChart } from "../../../constants/charts.enums";
import { useChartsContext } from "../../../contexts";
import { useChartEntities } from "../../../hooks/charts";
import { useMaxBinDefaults } from "../../../hooks/useMaxBinDefaults";
import { useUserEvents } from "../../../hooks/useUserEvents";
import useUserUnit from "../../../hooks/useUserUnit";
import { BinType, ColumnGroup, DataEnum, IGroupBy, QuantileState } from "../../../models";
import { PdenSourceEnum } from "../../../models/pdenDataSourceSetting";
import { updateChartByPath } from "../../../store/features";
import { RootState } from "../../../store/rootReducer";
import {
  selectUseMcDanielForecasts,
  selectUseNoviForecasts,
  selectUseWorkingForecasts,
  useForecastStore
} from "../../../stores/forecast";
//import { updateIsJitterPlotXBinDefaulting } from "../../chart";
import { visibilityToggleStyles } from "../../shared/SharedStyles";
import {
  getSyncedSchemaFromProperty,
  isSyncFolder,
  numberToRescatString
} from "../../sync/util";
import useAxisDataTypeFilter from "../hooks/useAxisDataTypeFilter";
import useAxisFieldSelection from "../hooks/useAxisFieldSelection";
import useAxisLogToggle from "../hooks/useAxisLogToggle";
import PressureChartOptions, {
  PRESSURE_CUM_SECTION,
  TOTAL_FLUID_CUM_SECTION
} from "../toggles/PressureChartOptions";
import StackedBarCalculation from "../toggles/stacked-bar-options/StackedBarCalculation";

const { Text } = Typography;
const dataRoot = process.env.REACT_APP_DATA_RS_ROOT;
const BIN_SIZE_AXIS_QUERY_KEY = "bin-size-axis";

// Module-level so React Query's cache — which holds the query function for cacheTime
// (5 minutes) past unmount, under a fresh key per filter — keeps no reference to a
// component closure, and through it to the chart tree. Every input is in the key.
const fetchAxisBinSize = ({ queryKey }: QueryFunctionContext) => {
  const [, filterId, property, entityKind] = queryKey as [string, string, string, string];
  return axios.get(
    `${dataRoot}/api/v1/data/bin-size/${filterId}/${btoa(
      encodeURIComponent(property)
    )}?normalizeField=%27%27&per=0&entityKind=${entityKind}`
  );
};

// EVA-306: the complete bin shape resetAxisBin already produces for a field
// with no numeric bin-size data of its own (BinType/BinSize/LessThan/
// GreaterThan/MinSize all present). withMaxBinDefault only ever adds
// MaxBins/MaxBinsSortOrder on top of whatever base it's given, so a selection
// default seeded from `{}` instead of this shape leaves those other keys
// missing entirely — which the chart-options translators read as "unbinned"
// and render zero x categories for, even though 27 series still come back.
const EMPTY_CATEGORY_AXIS_BIN: mBinSize = {
  BinType: "BinSize",
  BinSize: 0,
  LessThan: null,
  GreaterThan: null,
  MinSize: null
} as mBinSize;

const HIDE_OTHERS_TOOLTIP = 'Hide "Others" and "Less than" bins';

const sortOptions = [
  {
    icon: mdiSort,
    value: "",
    tooltip: "Unsorted"
  },
  {
    icon: mdiSortDescending,
    value: "desc",
    tooltip: "Sort descending"
  },
  {
    icon: mdiSortAscending,
    value: "asc",
    tooltip: "Sort ascending"
  }
];

function ChartAxisFieldSelector({ type = null, className = "" }) {
  const xAxis = type === AXIS_TYPE.x;
  const yAxis = type === AXIS_TYPE.y;
  // The y-axis buttons stack vertically, so a tooltip on top would cover the button above.
  const buttonTooltipPlacement = yAxis ? "right" : "top";

  const { id, entityKind } = useChartsContext();

  const { sendChartAxisFieldSelectorEvent } = useUserEvents();
  const dispatch = useDispatch();
  const unitType = useUserUnit();
  const filterId = useSelector((state: RootState) =>
    entityKind == EntityKind.Well ? state.filter.filterId : state.facilityFilter.filterId
  );
  const widgetHoverMap = useAtomValue(widgetHoverAtom);
  const globalGroupBy = useSelector((state: RootState) => state.groupBy.globalGroupBy);
  const globalFacilityGroupBy = useSelector(
    (state: RootState) => state.groupBy.globalFacilityFocus
  );

  const syncStatus = useSelector((state: RootState) => state.app.syncWells);
  // What the Forecasts panel currently implies, resolved the same way the Forecast Source
  // dropdown resolves it, so the committed axis and the dropdown cannot describe different data.
  const forecastMode = useSelector((state: RootState) => state.app.forecastMode);
  // isAuthoritative, not isResolved: getAccessibleDataSources catches every failure and resolves
  // { ok: false } rather than rejecting, so react-query reports success and never retries. One
  // transient 5xx would flip hasNoviAccess to false, which this reads as a panel change and now
  // PERSISTS as a cleared pick — losing the user's choice for good. Same guard as
  // useEffectiveForecastFolderId.
  const { hasNoviAccess, isAuthoritative: isNoviAccessAuthoritative } = useNoviAccess();
  const useMcDanielForecasts = useForecastStore(selectUseMcDanielForecasts);
  const useNoviForecasts = useForecastStore(selectUseNoviForecasts);
  const useWorkingForecasts = useForecastStore(selectUseWorkingForecasts);
  // Baseline for "has the panel selection actually changed", as opposed to the derived value
  // merely settling on mount — Novi entitlement is fetched, so it arrives false and flips.
  // A component-lifetime ref is the right lifetime for THIS: a fresh mount has seen no change
  // yet, which is exactly what we want it to conclude. The pick itself is persisted on the axis
  // instead (forecastFolderExplicit), because that one has to outlive the mount.
  //
  // hasBaseline is explicit rather than inferred from the ref being undefined, so this does not
  // silently depend on getForecastSourceFromSelection never returning undefined.
  const hasDerivedBaselineRef = useRef(false);
  const lastDerivedForAxisRef = useRef<Maybe<string>>(undefined);
  const derivedForecastFolderId = getForecastSourceFromSelection({
    forecastMode,
    useMcDanielForecasts,
    useNoviForecasts: useNoviForecasts && hasNoviAccess,
    useWorkingForecasts
  });
  const categories = useSelector((state: RootState) => state.groupBy.categoryList);
  const facilityFields = useSelector((state: RootState) => state.groupBy.facilityFields);

  // The forecast repair vouches a peeled base against the field list, and the two entity kinds
  // keep separate lists. Using the well list for a facility axis would either decline every
  // repair (facility-only session, categoryList empty) or fail the vouch outright (mixed
  // session, categoryList holds well fields) — and forecast is reachable on midstream charts,
  // so a facility axis carries the same qualification and corrupts the same way.
  const axisFieldList = entityKind === EntityKind.Facility ? facilityFields : categories;
  const {
    chartType,
    chartTypeParam: chartTypeParamState,
    pressureChartSettings,
    product,
    dataTable,
    screenshot,
    histogramToggle,
    jitterPlotToggle,
    calculationMethod,
    logAxis,
    multiProduct,
    options: optionString,
    xAxisFieldSync,
    xAxisSort,
    splitYear,
    hideOthers,
    apiResponse,
    request
  } = useChartEntities(id);
  const showLogToggle = useAxisLogToggle(type, chartType);
  const showFieldSelection = useAxisFieldSelection(
    type,
    chartType,
    histogramToggle.active,
    jitterPlotToggle.active
  );
  const axisDataTypeFilter = useAxisDataTypeFilter(type, chartType);

  const isLogScaleActive = logAxis?.properties[type]?.active;
  const paramField = chartTypeParamState.properties[type];
  const isHover = widgetHoverMap?.[id] ?? false;
  const showAxis = !screenshot?.active && !dataTable?.active && isHover;
  const isMosaicChart = IS_MOSAIC_CHART(chartType);
  const isStackedBarChart = IS_STACKED_BAR_CHART(chartType);
  const isStackedBarHorizontal = IS_STACKED_BAR_HORIZONTAL_CHART(chartType);
  const isBoxPlotChart = IS_BOX_PLOT_CHART(chartType);
  // Horizontal Stacked Bar swaps chartTypeParam's x/y roles (x = numeric,
  // y = category — see features/stackedBarHorizontal.ts), so "category" is x
  // for every other chart type but y for this one. A substitution for xAxis,
  // not an OR-addition: category controls live exclusively on this axis.
  const categoryAxisKey: "x" | "y" = isStackedBarHorizontal ? "y" : "x";
  const isCategoryAxis = type === categoryAxisKey;
  // The y-axis FieldContainer is rotated -90deg, which turns the category
  // controls' icons on their side once they move there — rotate them back.
  const categoryIconRotation = isStackedBarHorizontal && yAxis ? 90 : 0;
  // The sort icon is drawn with horizontal bars, so the vertical chart turns
  // it to match its vertical bars; the horizontal chart shows it upright. The box plot
  // sorts from its y axis, whose container is already rotated -90deg, so it adds nothing.
  const sortIconRotation = isStackedBarHorizontal
    ? categoryIconRotation
    : isBoxPlotChart
    ? 0
    : -90;
  const withCategoryAxisBin = (newBin: mBinSize) => ({
    ...chartTypeParamState.properties,
    [categoryAxisKey]: { ...chartTypeParamState.properties[categoryAxisKey], bin: newBin }
  });
  const isTrendDate = IS_TREND_DATE_CHART(chartType);
  const isJitterPlot = isBoxPlotChart && jitterPlotToggle?.active;

  // Swapping moves the category field into the numeric slot, so validity
  // depends on that field's dataType.
  const isValidAxesSwap =
    chartTypeParamState.properties[categoryAxisKey]?.dataType === "Number" ||
    chartTypeParamState.properties[categoryAxisKey]?.dataType === "Integer";

  const isRateCum = chartType === EvaChart.RateCum;
  const isRateTime = chartType === EvaChart.RateTime;

  // In MPC the plotted products are otherProducts; `product` is only the one MPC was entered from.
  const plottedProductKeys = (
    multiProduct?.active ? multiProduct?.properties?.otherProducts ?? [] : [product]
  ).map((p) => getProductType(p, entityKind)?.key);
  const plotsProduct = (p: IProductType) => plottedProductKeys.includes(p.key);
  // Casing and Tubing share one setting, so the section names whichever of them are plotted.
  const plottedPressureLabels = [
    ALL_PRODUCT_TYPES.CasingPressure,
    ALL_PRODUCT_TYPES.TubingPressure
  ]
    .filter(plotsProduct)
    .map((p) => p.label);
  const cumProductSections = [
    ...(plottedPressureLabels.length > 0 &&
    (isRateCum || (isRateTime && pressureChartSettings?.active))
      ? [{ ...PRESSURE_CUM_SECTION, title: plottedPressureLabels.join(" / ") }]
      : []),
    ...(plotsProduct(ALL_PRODUCT_TYPES.TotalFluid) &&
    (isRateCum || chartType === EvaChart.TotalRateCum)
      ? [TOTAL_FLUID_CUM_SECTION]
      : [])
  ];
  const isCalcMethodCount =
    calculationMethod?.properties?.method === CalculationMethod.Count;
  const isHistogramOn = histogramToggle?.active;
  const maxBinDefaultsEnabled = useMaxBinDefaults() === true;
  const [syncToFocusField, setSyncToFocusField] = useState(false);
  // The unsync reset effect below also runs once on mount (syncToFocusField
  // starts false); this tells that run apart from a real unsync.
  const unsyncResetHasRunRef = useRef(false);
  const [sortOptionIndex, setSortOptionIndex] = useState(0);
  const [axisBinOpen, setAxisBinOpen] = useState(false);
  // The bin popover portals outside this axis's DOM subtree, so hiding its
  // trigger wouldn't hide an already-open panel — force-close it instead.
  useEffect(() => {
    if (isHistogramOn) setAxisBinOpen(false);
  }, [isHistogramOn]);
  // Sync can flip from the saved xAxisFieldSync flag, not only an outside click.
  useEffect(() => {
    if (syncToFocusField) setAxisBinOpen(false);
  }, [syncToFocusField]);
  const [bin, setBin] = useState<mBinSize>({
    LessThan: null,
    GreaterThan: null,
    MinSize: null,
    BinSize: null,
    BinType: "BinSize"
  });

  // Resets the axis locks
  // If axis is not provided, resets all axis locks
  // If axis is provided, resets only the specified axis locks
  const resetAxisLocks = (axis?: "x" | "y") => {
    const resetLocks = {
      value: undefined,
      type: DataEnum.Reset,
      override: false
    };
    // Reset reads as a user-removed lock and would suppress the Trend Date 2000 default
    const resetXMin = isTrendDate ? UNDEFINED_LOCKS.xMin : resetLocks;
    const resetXMax = isTrendDate ? UNDEFINED_LOCKS.xMax : resetLocks;

    if (!axis) {
      dispatch(
        updateChartByPath({
          id,
          path: `locks`,
          value: {
            xMin: resetXMin,
            xMax: resetXMax,
            yMin: resetLocks,
            yMax: resetLocks
          }
        })
      );
    } else {
      dispatch(
        updateChartByPath({
          id,
          path: `locks.${axis}Min`,
          value: axis === "x" ? resetXMin : resetLocks
        })
      );
      dispatch(
        updateChartByPath({
          id,
          path: `locks.${axis}Max`,
          value: axis === "x" ? resetXMax : resetLocks
        })
      );
    }
  };

  const updateForecastSourceToCurrentSchema = () => {
    const chartTypeParamGroupBy = chartTypeParamState.properties[type] as GroupByValueT;
    if (!chartTypeParamGroupBy) {
      return;
    }
    const syncedSchemaFromProperty = getSyncedSchemaFromProperty(chartTypeParamGroupBy);
    if (
      syncStatus &&
      syncStatus.schema !== "" &&
      syncStatus.schema !== syncedSchemaFromProperty &&
      syncStatus.isSync &&
      syncStatus.useFcstFromClient &&
      chartTypeParamGroupBy.property.indexOf(FORECAST_KEY) > 0
    ) {
      let newPropertyWithCurrentSchema = chartTypeParamGroupBy.property;
      if (newPropertyWithCurrentSchema.endsWith(FORECAST_KEY)) {
        newPropertyWithCurrentSchema = `${newPropertyWithCurrentSchema}.${
          syncStatus.schema
        }.${numberToRescatString(syncStatus.forecastRescat)}`;
      } else if (syncedSchemaFromProperty) {
        newPropertyWithCurrentSchema = newPropertyWithCurrentSchema.replace(
          syncedSchemaFromProperty,
          syncStatus.schema
        );
      } else {
        return;
      }
      const groupBy = {
        ...chartTypeParamState.properties[type],
        property: newPropertyWithCurrentSchema,
        forecastFolder: syncStatus.schema,
        isForecastToggleOn: true,
        normalizeByDefault: 100
      };
      if (!groupBy.pdenSource) {
        //add pden source if it doesn't exist and set it to Hybrid
        //this is because when switching chart types, the pden source is not set
        groupBy.pdenSource = PdenSourceEnum.Hybrid;
      }
      dispatch(
        updateChartByPath({
          id,
          path: `features.chartTypeParam.properties.${type}`,
          value: groupBy
        })
      );
    }
  };

  useEffect(() => {
    updateForecastSourceToCurrentSchema();
  }, [syncStatus]);

  // Keeps a committed axis in step with the Forecasts panel.
  //
  // The axis property and its forecastFolder are stamped when a field is picked, and nothing
  // re-stamps them afterwards. So a chart saved while only EVA Research was ticked keeps EVA
  // Research even once Novi is ticked too — the Forecast Source dropdown resolves to Forecast
  // Trumping and says so, while the request it describes still asks for EVA Research alone.
  //
  // Only widens to trumping, and only for a forecast-bearing axis that is not already trumping.
  // Narrowing is deliberately left alone: unticking a source is handled by the dropdown, and
  // rewriting a saved axis in that direction would discard a choice the user made explicitly.
  const requalifyAxisForForecastTrumping = () => {
    const axisGroupBy = chartTypeParamState.properties[type] as IGroupBy;

    // A changed panel selection is a fresh instruction and supersedes an earlier pick here,
    // matching how the Forecast Source dropdown treats the same event.
    //
    // Gated on the Novi entitlement being authoritative. It is fetched, so the derived value
    // starts at whatever "no Novi" implies and flips once the answer lands. Treating that
    // settling as a panel change would clear a restored pick on every mount, which is the bug
    // this marker exists to prevent.
    let isExplicitPick = axisGroupBy?.forecastFolderExplicit;
    let shouldClearExplicitPick = false;
    let panelSelectionChanged = false;
    if (
      isNoviAccessAuthoritative &&
      lastDerivedForAxisRef.current !== derivedForecastFolderId
    ) {
      // Mount is not a change — the first settled value only establishes the baseline.
      const isFirstSettledValue = !hasDerivedBaselineRef.current;
      hasDerivedBaselineRef.current = true;
      lastDerivedForAxisRef.current = derivedForecastFolderId;
      panelSelectionChanged = !isFirstSettledValue;
      if (panelSelectionChanged && isExplicitPick) {
        isExplicitPick = false;
        shouldClearExplicitPick = true;
      }
    }

    // Repairs an axis the forecast toggle corrupted before that was fixed. Placed after the
    // bookkeeping above so a panel change that coincides with a repair is still recorded, and
    // ahead of the explicit-pick gate below because a malformed property is not a choice worth
    // preserving. Nothing else revisits these: the widen only fires on an axis that is not yet
    // trumping, and it splits at the first marker, so on a corrupt property it welds the orphan
    // into the base and leaves a shape neither path can see again.
    //
    // Every qualification this axis could be carrying: each forecast source it could have been
    // stamped under, crossed with every reserve category. Naming them explicitly is what makes the
    // repair safe — the source key lives only in the property, never on the axis, since
    // forecastFolder holds the display name — and enumerating the rescats rather than assuming the
    // default is what lets an axis corrupted under PDP or TP be recovered instead of left blank.
    // The list is still not exhaustive (a project forecast folder is its own source), which is why
    // the peeled base is vouched below rather than trusted.
    const orphanedQualificationCandidates = [
      FORECAST_TRUMPING_FOLDER_ID,
      NOVI_FORECAST_SOURCE,
      WORKING_FORECAST_SOURCE,
      derivedForecastFolderId,
      syncStatus?.isSync ? syncStatus.schema : undefined
    ]
      .filter(Boolean)
      .flatMap((source) =>
        ALL_RESCATS.map((rescat) => getForecastPropertySuffix(source, rescat))
      );

    const repaired = axisGroupBy
      ? repairOrphanedForecastProperty(
          axisGroupBy.property,
          orphanedQualificationCandidates,
          // The candidate list cannot be complete — every project forecast folder is offered
          // as its own source — so the peeled base is checked against the field list. An
          // unrecognised source declines instead of having a segment welded into its base.
          (base) => !!getGroupByListColumnByProperty(axisFieldList, base)
        )
      : null;
    if (repaired) {
      dispatch(
        updateChartByPath({
          id,
          path: `features.chartTypeParam.properties.${type}`,
          value: {
            ...axisGroupBy,
            // The bookkeeping above already recorded the panel change, so this return is the
            // last chance to act on it — dropping it here would pin the pick permanently and
            // gate off the widen for the life of the axis.
            ...(shouldClearExplicitPick ? { forecastFolderExplicit: false } : {}),
            property: repaired.property,
            // Non-empty only for the forecast-off shape, where the qualification comes out of
            // the property and has to be parked for the next toggle-on to restore.
            //
            // Dropped outright when this pass is also a panel change: the bookkeeping above has
            // already advanced the refs, so no later pass sees that change, and this return
            // skips the branch below that would otherwise clear it. Parking under a superseded
            // selection would restore a source the panel no longer names.
            forecastPropertySuffix: panelSelectionChanged
              ? undefined
              : repaired.parkedQualification || undefined
          }
        })
      );
      return;
    }

    // A stash outlives the refs that detect a panel change — they are component-lifetime, it is
    // persisted with the axis — so a remount into a session with a different selection keeps it
    // and the next toggle-on rebuilds a source the panel does not name. The widen cannot correct
    // that: while forecast is off the property is bare, so its qualified-property gate fails.
    if (
      axisGroupBy?.forecastPropertySuffix &&
      isNoviAccessAuthoritative &&
      derivedForecastFolderId &&
      isStaleForecastQualification(
        axisGroupBy.forecastPropertySuffix,
        derivedForecastFolderId
      )
    ) {
      dispatch(
        updateChartByPath({
          id,
          path: `features.chartTypeParam.properties.${type}`,
          value: { ...axisGroupBy, forecastPropertySuffix: undefined }
        })
      );
      return;
    }

    if (isExplicitPick) {
      return;
    }

    // A property whose base the field list cannot vouch for is one we do not understand — most
    // likely corrupt under a forecast source the repair above could not identify. Widening it
    // splits at the first marker, welds the orphan into the base and stamps trumping on top,
    // leaving a shape neither the repair nor the widen can see again.
    //
    // Requires the list to be loaded rather than excusing the widen while it is in flight. An
    // empty list vouches for nothing, so the repair above already declines in that window;
    // letting the widen act anyway would have the safe path wait while the destructive one
    // proceeds, and its entitlement gate resolves from a smaller fetch than this list does, so
    // that ordering is the likely one. Nothing is lost by waiting — the list is in the effect
    // deps, so the widen simply runs on the pass where it can be checked.
    // Only a terminal marker can be the corrupt-on shape, so only that shape is held back for
    // the list. A healthy named-source property has its marker mid-string and widens straight
    // away — which matters because getCategoryList builds categories conditionally (project
    // fields, org geo behind a flag, schema columns per environment), so vetoing every axis on
    // an absent category would stop widening healthy ones.
    const couldBeCorrupt = (axisGroupBy?.property ?? "").endsWith(`.${FORECAST_KEY}`);
    const baseIsRecognised =
      !couldBeCorrupt ||
      (!!axisFieldList?.length &&
        !!getGroupByListColumnByProperty(
          axisFieldList,
          splitForecastQualification(axisGroupBy?.property ?? "").base
        ));

    const canWiden =
      axisGroupBy &&
      derivedForecastFolderId === FORECAST_TRUMPING_FOLDER_ID &&
      !isForecastTrumpingSource(axisGroupBy.forecastFolder) &&
      isForecastQualifiedProperty(axisGroupBy.property) &&
      baseIsRecognised &&
      !isSyncFolder(axisGroupBy.forecastFolder);

    if (!canWiden) {
      // The panel still superseded the pick even where there is nothing to widen — clear the
      // marker so a later change is not silently held off by it.
      //
      // The parked qualification is cleared on any panel change, not only one that also
      // supersedes a pick: while forecast is off the property is bare, so this is the branch
      // taken, and a stash left behind would restore a source the panel no longer selects.
      // Widening rescues only one direction — untick Novi and nothing else would revisit it.
      if (
        shouldClearExplicitPick ||
        (panelSelectionChanged && axisGroupBy?.forecastPropertySuffix)
      ) {
        dispatch(
          updateChartByPath({
            id,
            path: `features.chartTypeParam.properties.${type}`,
            value: {
              ...axisGroupBy,
              forecastFolderExplicit: false,
              // The stash follows the same rule as the marker beside it: a panel change is a
              // fresh instruction, so a qualification parked under the old selection must not
              // be replayed on the next toggle-on.
              forecastPropertySuffix: undefined
            }
          })
        );
      }
      return;
    }
    // One dispatch, not two: writing the cleared marker separately would leave this spread
    // working from a stale axisGroupBy and stamp the old value straight back.
    dispatch(
      updateChartByPath({
        id,
        path: `features.chartTypeParam.properties.${type}`,
        value: {
          ...axisGroupBy,
          // Forecast Trumping offers exactly one rescat, so there is nothing per-axis to
          // recover here — a fresh pick under trumping produces this same suffix.
          property: requalifyForecastProperty(
            axisGroupBy.property,
            FORECAST_TRUMPING_FOLDER_ID,
            DEFAULT_RESCAT
          ),
          forecastFolder: FORECAST_TRUMPING_FOLDER_ID,
          forecastFolderExplicit: false,
          forecastPropertySuffix: undefined
        }
      })
    );
  };

  useEffect(() => {
    requalifyAxisForForecastTrumping();
  }, [
    derivedForecastFolderId,
    isNoviAccessAuthoritative,
    chartTypeParamState.properties[type]?.property,
    // The repair reads syncStatus to build its sync candidate, and syncStatus is fetched: on
    // the passes that run before it lands the candidate is empty and a corrupt synced axis
    // declines. Without this dep nothing re-runs once it arrives, so that axis keeps asking
    // for the orphaned property until something unrelated moves.
    syncStatus,
    // Same reason, one layer down: the base check is only as good as the list behind it, and
    // an empty list declines every repair and defers every widen.
    axisFieldList
  ]);

  const updateLog = (isActive: boolean) => {
    if (xAxis) {
      dispatch(
        updateChartByPath({
          id,
          path: "features.logAxis.properties.x.active",
          value: isActive
        })
      );
    }
    if (yAxis) {
      dispatch(
        updateChartByPath({
          id,
          path: "features.logAxis.properties.y.active",
          value: isActive
        })
      );
    }
  };

  const swapAxesParamFields = () => {
    if (syncToFocusField) {
      setSyncToFocusField(false);
      dispatch(
        updateChartByPath({
          id,
          path: "features.xAxisFieldSync.active",
          value: false
        })
      );
    }
    resetAxisLocks();
    dispatch(
      updateChartByPath({
        id,
        path: "features.chartTypeParam.properties",
        value: {
          x: { ...chartTypeParamState.properties.y },
          y: { ...chartTypeParamState.properties.x }
        }
      })
    );
  };

  const updateParamField = (
    groupBy: IGroupBy,
    selectedPdenSource: PdenSourceEnum,
    performanceDateRange?: { startDate: string; endDate: string }
  ) => {
    const isJitterPlotXBinDefaulting = isJitterPlot && type === "x";

    const updatedGroupBy = { ...groupBy };
    if (groupBy.units && groupBy.units[unitType]) {
      updatedGroupBy.defaultUnit = groupBy.units[unitType];
    }

    // EVA-306: prefill Max Bins for a bar-chart category-axis selection with
    // no max bins of its own. A synced axis follows the global bin instead
    // (R3a), and an incoming bin that already carries MaxBins is left alone.
    if (
      maxBinDefaultsEnabled &&
      isCategoryAxis &&
      !syncToFocusField &&
      (isStackedBarChart || isStackedBarHorizontal) &&
      !updatedGroupBy.bin?.MaxBins
    ) {
      const catalogColumn = findCatalogColumn(axisFieldList, updatedGroupBy.property);
      // Only touch `bin` when there's an actual default to apply -- calling
      // withMaxBinDefault unconditionally turned a genuinely undefined bin
      // (a field without a default) into `{}`.
      if (getMaxBinDefault(catalogColumn, entityKind) != null) {
        updatedGroupBy.bin = withMaxBinDefault(
          updatedGroupBy.bin ?? EMPTY_CATEGORY_AXIS_BIN,
          catalogColumn,
          entityKind
        );
      }
    }

    sendChartAxisFieldSelectorEvent({
      chartType: chartType,
      axis: type,
      entityKind: entityKind,
      field: updatedGroupBy.property
    });

    resetAxisLocks(type);
    if (isJitterPlotXBinDefaulting) {
      dispatch(
        updateChartByPath({
          id,
          path: "features.jitterPlotToggle.properties",
          value: {
            xBinDefaulting: true
          }
        })
      );
    }
    dispatch(
      updateChartByPath({
        id,
        path: `features.chartTypeParam.properties.${type}`,
        value: {
          ...updatedGroupBy,
          pdenSource: selectedPdenSource,
          performanceDateRange
        }
      })
    );
  };

  const activeGroupBy =
    entityKind === EntityKind.Well ? globalGroupBy : globalFacilityGroupBy;

  const isDatesGroupField =
    IS_CROSS_PLOT_CHART(chartType) &&
    activeGroupBy.group === ColumnGroup.Dates &&
    activeGroupBy.subgroup !== ColumnGroup.Dates &&
    activeGroupBy.subgroup !== ColumnGroup.General;

  const splitYearUnit = getSplitYearUnit(chartTypeParamState.properties[categoryAxisKey]);
  // Split from isSplitYearAvailable's histogram check so the button stays
  // mounted (hidden, not unmounted) while histogram is on — see HideableControl.
  const isSplitYearStructurallyPossible =
    (isStackedBarChart || isStackedBarHorizontal) &&
    isCategoryAxis &&
    splitYearUnit !== null;
  const isSplitYearAvailable =
    (isStackedBarChart || isStackedBarHorizontal) &&
    isCategoryAxis &&
    splitYearUnit !== null &&
    !isHistogramOn;
  const splitYearActive = splitYear?.active ?? false;

  // Link to Axis sits beside Hide Others: a stacked bar category axis whose field has
  // Max Bins.
  const showsMaxBinControls =
    (isStackedBarChart || isStackedBarHorizontal) &&
    isCategoryAxis &&
    supportsMaxBins(paramField.canBin, paramField.dataType);
  const linkedAxisLabel = showsMaxBinControls
    ? getLinkToAxisLabel(
        calculationMethod?.properties?.method,
        chartTypeParamState.properties[isStackedBarHorizontal ? "x" : "y"]?.title,
        entityKind
      )
    : undefined;
  const isHidingOthers = !!hideOthers?.active;
  // Since the reshape is backend-driven, toggling it means waiting on a real network round
  // trip, not an instant client-side recompute. `status` alone isn't precise enough to gate
  // on: usePostChartData debounces the fetch by 300ms, so `status` doesn't flip to `Loading`
  // until after that timer fires — for that whole window right after the click, `status` is
  // still `Idle` but `apiResponse` is still the stale, pre-toggle response. Instead, compare
  // the actual request that produced the current `apiResponse` (dispatched into Redux
  // alongside it, see useChartDataController) against what the request would look like right
  // now — if they don't match, `apiResponse` hasn't caught up to this toggle yet, so don't
  // trust its splitYearApplied value.
  const splitYearResponseFresh =
    splitYearActive && isSplitYearResponseFresh(request, splitYearActive, isHistogramOn);
  // Field-config bug (frontend/backend eligibility checks disagree) vs. a genuine "this
  // field's data has no quarter/month buckets" — see isSplitYearFieldMismatch. These need
  // different messaging: the mismatch case isn't something the user can fix by looking at
  // their data, so it shouldn't be presented as if it were.
  const splitYearFieldMismatch =
    splitYearResponseFresh &&
    apiResponse?.splitYearApplied === false &&
    isSplitYearFieldMismatch(
      chartTypeParamState.properties[categoryAxisKey],
      apiResponse?.splitYearDetectedUnit
    );
  const splitYearNoData =
    splitYearResponseFresh &&
    apiResponse?.splitYearApplied === false &&
    !splitYearFieldMismatch;

  // A frontend/backend eligibility disagreement is a field-config bug, not something a user
  // caused — report it so it gets fixed, instead of only showing a misleading "no data" toast.
  useEffect(() => {
    if (splitYearFieldMismatch) {
      Sentry.withScope((scope) => {
        scope.setContext("splitYearFieldMismatch", {
          xFieldMappingName: chartTypeParamState.properties[categoryAxisKey]?.mappingName,
          xFieldDefaultUnit: chartTypeParamState.properties[categoryAxisKey]?.defaultUnit,
          detectedUnit: apiResponse?.splitYearDetectedUnit
        });
        scope.setLevel("warning");
        Sentry.captureMessage(
          "Split Year: frontend field.defaultUnit and backend mapping-name eligibility disagree"
        );
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [splitYearFieldMismatch]);

  // Split Year's availability is field-scoped (see isSplitYearAvailable above) — the button
  // only renders for x fields with a Year-Qtr/Year-Month unit. Without this, switching to an
  // ineligible field hides the button but leaves the Redux flag on, so splitYearActive keeps
  // being sent as true on every subsequent request (for any field) until the user happens to
  // land back on an eligible one and turns it off manually.
  useEffect(() => {
    if (isCategoryAxis && !isSplitYearAvailable && splitYearActive) {
      dispatch(
        updateChartByPath({
          id,
          path: "features.splitYear.active",
          value: false
        })
      );
    }
  }, [isCategoryAxis, isSplitYearAvailable, splitYearActive, dispatch, id]);

  // Keep the local toggle in step with the persisted flag across remounts.
  useEffect(() => {
    setSyncToFocusField(xAxisFieldSync?.active ?? false);
  }, [xAxisFieldSync?.active]);

  // Keep the sort icon in step with the persisted value across remounts —
  // otherwise a chart saved/loaded with a non-default sort shows "Unsorted"
  // until the button is clicked once.
  useEffect(() => {
    const persistedIndex = sortOptions.findIndex(
      (option) => option.value === (xAxisSort?.properties?.value ?? "")
    );
    setSortOptionIndex(persistedIndex === -1 ? 0 : persistedIndex);
  }, [xAxisSort?.properties?.value]);

  useEffect(() => {
    if (
      !isCategoryAxis ||
      (axisDataTypeFilter.length > 0 &&
        !axisDataTypeFilter.includes(activeGroupBy.dataType) &&
        !isDatesGroupField) ||
      !syncToFocusField
    ) {
      return;
    }
    const gb = activeGroupBy;
    const groupByWithoutBin = { ...gb };

    //cross plot doesn't need bin
    delete groupByWithoutBin.bin;

    const source = stringToEnum(PdenSourceEnum, gb.pdenSource);
    let pdenSource = PdenSourceEnum.Public;

    if (source && typeof source === "string") {
      pdenSource = PdenSourceEnum[gb.pdenSource];
    } else if (source) {
      pdenSource = source;
    }

    updateParamField(
      groupByWithoutBin,
      //we need the numeric value of the enum
      pdenSource
    );
  }, [globalGroupBy, globalFacilityGroupBy, syncToFocusField, axisDataTypeFilter]);

  useEffect(() => {
    if (isCategoryAxis && syncToFocusField) {
      // setTimeout to prevent overriding chartTypeParamState update
      const timeoutId = setTimeout(() => {
        setXAxisBinFromSessionStorage();
      }, 0);
      return () => clearTimeout(timeoutId);
    }
  }, [
    syncToFocusField,
    chartTypeParamState.properties[type]?.property,
    globalGroupBy.bin,
    globalFacilityGroupBy.bin
  ]);

  const { data: binRequest, refetch } = useQuery(
    [BIN_SIZE_AXIS_QUERY_KEY, filterId.id, paramField.property, entityKind],
    fetchAxisBinSize,
    {
      enabled: false
    }
  );

  const updateAxisBin = (binType: BinType, quantile: QuantileState) => {
    const updatedBin = Object.assign({}, bin, {
      BinType: binType,
      Quantile: quantile
    });
    setBin(updatedBin);
    dispatch(
      updateChartByPath({
        id,
        path: "features.chartTypeParam.properties",
        value: withCategoryAxisBin(updatedBin)
      })
    );
  };

  function setXAxisBinFromSessionStorage() {
    const chartTypeParamGroupBy = chartTypeParamState.properties[type] as GroupByValueT;
    const sessionBin = getStoredBinsFromSession();
    if (sessionBin && chartTypeParamGroupBy.property in sessionBin) {
      const sessionBinData = sessionBin[chartTypeParamGroupBy.property];
      // Convert empty strings to null
      const sanitizedSessionBin = Object.keys(sessionBinData).reduce((acc, key) => {
        acc[key] = sessionBinData[key] === "" ? null : sessionBinData[key];
        return acc;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      }, {} as any);
      // A synced axis ranks by the focus criteria, so it opts out of the Link to Axis default.
      const newBin = Object.assign({}, bin, sanitizedSessionBin, {
        MaxBinsLinkToAxis: false
      });
      dispatch(
        updateChartByPath({
          id,
          path: "features.chartTypeParam.properties",
          value: withCategoryAxisBin(newBin)
        })
      );
      setBin(newBin);
    }
  }

  function setAxisBin(val: mBinSize) {
    if ((!syncToFocusField && isCategoryAxis) || !isCategoryAxis) {
      const newBin = Object.assign({}, bin, val);
      setBin(newBin);
    }
  }

  async function resetAxisBin() {
    if (!filterId.id || filterId.id === "") return;
    const result = await refetch();
    const data = result.data?.data;
    // EVA-306: Reset Default gives the catalog value for a bar-chart category
    // axis, under the same conditions as selection. A field without one keeps
    // data?.maxBins (always undefined — the bin-size endpoint never returns it).
    const maxBinsDefault =
      maxBinDefaultsEnabled &&
      isCategoryAxis &&
      !syncToFocusField &&
      (isStackedBarChart || isStackedBarHorizontal)
        ? getMaxBinDefault(
            findCatalogColumn(axisFieldList, paramField.property),
            entityKind
          )
        : null;
    const newBin = {
      LessThan: data?.lessThan,
      GreaterThan: data?.greaterThan,
      MinSize: data?.minSize,
      MaxBins: maxBinsDefault ?? data?.maxBins,
      ...(maxBinsDefault != null ? { MaxBinsSortOrder: "WellCount" as const } : {}),
      BinSize: data?.binSize,
      BinType: "BinSize"
    } as mBinSize;
    dispatch(
      updateChartByPath({
        id,
        path: "features.chartTypeParam.properties",
        value: withCategoryAxisBin(newBin)
      })
    );
    setBin(newBin);
    setAxisBinOpen(false);
  }

  useEffect(() => {
    // A cached answer for another filter id lands here too; it never replaces a bin the axis owns.
    if (binRequest?.data?.isValid && !paramField.bin) {
      const newBin = {
        LessThan: binRequest.data.lessThan,
        GreaterThan: binRequest.data.greaterThan,
        MinSize: binRequest.data.minSize,
        MaxBins: binRequest.data.maxBins,
        MaxBinsSortOrder: binRequest.data.MaxBinsSortOrder,
        BinSize: binRequest.data.binSize,
        BinType: "BinSize"
      } as mBinSize;
      setBin(newBin);
    }
  }, [binRequest]);

  useEffect(() => {
    if (!isCategoryAxis) return;
    if (paramField.bin) {
      setBin(paramField.bin);
    } else {
      setBin({
        LessThan: null,
        GreaterThan: null,
        MinSize: null,
        BinSize: null,
        BinType: "BinSize"
      });
    }
  }, [paramField.property]);

  useEffect(() => {
    const isMountRun = !unsyncResetHasRunRef.current;
    unsyncResetHasRunRef.current = true;
    if (isCategoryAxis && !syncToFocusField && filterId.id && filterId.id !== "") {
      // EVA-306: a mount (reload, dashboard switch) keeps a restored, unsynced
      // bar-chart category-axis bin that carries Max Bins instead of wiping it.
      if (
        isMountRun &&
        maxBinDefaultsEnabled &&
        (isStackedBarChart || isStackedBarHorizontal) &&
        !xAxisFieldSync?.active &&
        paramField.bin?.MaxBins
      ) {
        return;
      }
      // EVA-306: an unsynced bar-chart field falls back to its catalog
      // default. Written as the whole axis because a synced axis has no `bin`
      // key, and updateChartByPath ignores paths that don't exist.
      const catalogColumn = findCatalogColumn(axisFieldList, paramField.property);
      if (
        !isMountRun &&
        maxBinDefaultsEnabled &&
        (isStackedBarChart || isStackedBarHorizontal) &&
        getMaxBinDefault(catalogColumn, entityKind) != null
      ) {
        const catalogDefaultBin = withMaxBinDefault(
          EMPTY_CATEGORY_AXIS_BIN,
          catalogColumn,
          entityKind
        );
        setBin(catalogDefaultBin);
        dispatch(
          updateChartByPath({
            id,
            path: "features.chartTypeParam.properties",
            value: withCategoryAxisBin(catalogDefaultBin)
          })
        );
        return;
      }
      refetch();
      dispatch(
        updateChartByPath({
          id,
          path: `features.chartTypeParam.properties.${categoryAxisKey}.bin`,
          value: null
        })
      );
    }
  }, [syncToFocusField]);

  const axisInputClassnames = classnames({ xAxis, yAxis });

  // In MP mode, if no Y-axis is on the left side, move the log toggle to the right
  // to avoid overlapping with lock icons.
  const hasNoLeftYAxis = (() => {
    if (!multiProduct?.active || !yAxis) return false;
    try {
      const opts = JSON.parse(optionString || "{}");
      const yAxisList = Array.isArray(opts.yAxis) ? opts.yAxis : [opts.yAxis ?? {}];
      return yAxisList.every((a) => a.position === "right");
    } catch {
      return false;
    }
  })();

  // In MP mode, if no X-axis is on the bottom, adjust the X-axis log toggle position
  const hasBottomXAxis = (() => {
    if (!multiProduct?.active) return true;
    try {
      const opts = JSON.parse(optionString || "{}");
      const xAxisList = Array.isArray(opts.xAxis) ? opts.xAxis : [opts.xAxis ?? {}];
      return xAxisList.some((axis) => axis?.position === "bottom");
    } catch {
      return true;
    }
  })();

  const hasSecondaryProduct =
    !!multiProduct?.active &&
    (multiProduct?.properties?.secondaryProducts?.length ?? 0) > 0;

  const logToggleClassnames = classnames({
    xAxis,
    yAxis,
    visible: showLogToggle && showAxis,
    yLogPositionAdjusted: hasNoLeftYAxis,
    xLogPositionAdjusted: hasSecondaryProduct,
    noBottomXAxis: !hasBottomXAxis
  });
  const fieldContainerClassName = classnames(axisInputClassnames, {
    visible: showFieldSelection && showAxis,
    stackUp: isHover
  });
  const wrapperClassnames = classnames(className, {
    xAxis,
    yAxis,
    stackDown: isHover,
    yLogPositionAdjusted: hasNoLeftYAxis
  });

  const isFieldPickerDisabled =
    (yAxis && isStackedBarChart && isCalcMethodCount) ||
    (xAxis && isStackedBarHorizontal && isCalcMethodCount);

  async function handleStackedBarCalculationChange(method: CalculationMethod) {
    // The calc method changes the numeric axis's scale — y normally, x for
    // the horizontal chart (its roles are swapped; see categoryAxisKey).
    resetAxisLocks(isStackedBarHorizontal ? "x" : "y");
    dispatch(
      updateChartByPath({
        id,
        path: "features.calculationMethod.properties",
        value: {
          method: method
        }
      })
    );
    if (filterId.id && filterId.id !== "") {
      await refetch();
    }
  }

  const cycleSortOption = () => {
    const newSortOptionIndex = (sortOptionIndex + 1) % 3;

    setSortOptionIndex(newSortOptionIndex);

    dispatch(
      updateChartByPath({
        id,
        path: "features.xAxisSort.properties.value",
        value: sortOptions[newSortOptionIndex].value // Use the calculated new value
      })
    );
  };

  // Compute correct title with current unit system
  const correctedTitle = useMemo(() => {
    if (!paramField?.title || unitType === undefined) {
      return paramField?.title || "";
    }

    // Try to get units from paramField first, otherwise look up from category list
    let units = paramField.units;
    let defaultUnit = paramField.defaultUnit;
    if (!units && paramField.property && categories) {
      const formattedProperty = formatPropertyName(paramField.property);
      const fieldDefinition = getGroupByListColumnByProperty(
        categories,
        formattedProperty
      );
      units = fieldDefinition?.units;
      defaultUnit = fieldDefinition?.defaultUnit || defaultUnit;
    }

    const correctUnit = units?.[unitType] || defaultUnit;
    if (!correctUnit) return paramField.title;

    if (paramField.title.includes(`(${correctUnit})`)) {
      return paramField.title;
    }

    // If title contains a unit in parentheses, replace it with correct unit
    if (paramField.title.includes("(") && paramField.title.includes(")")) {
      return paramField.title.replace(/\([^)]*\)/, `(${correctUnit})`);
    }

    return `${paramField.title} (${correctUnit})`;
  }, [
    paramField?.title,
    paramField?.units,
    paramField?.defaultUnit,
    paramField?.property,
    unitType,
    categories
  ]);

  // Update Redux when title or defaultUnit needs correction
  useEffect(() => {
    if (!paramField || unitType === undefined) return;

    // Try to get units from paramField first, otherwise look up from category list
    let units = paramField.units;
    let defaultUnit = paramField.defaultUnit;
    if (!units && paramField.property && categories) {
      const formattedProperty = formatPropertyName(paramField.property);
      const fieldDefinition = getGroupByListColumnByProperty(
        categories,
        formattedProperty
      );
      units = fieldDefinition?.units;
      defaultUnit = fieldDefinition?.defaultUnit || defaultUnit;
    }

    // Get the correct unit for the user's unit system, fallback to defaultUnit
    const correctUnit = units?.[unitType] || defaultUnit;
    if (!correctUnit) return;

    const hasParentheses =
      paramField.title?.includes("(") && paramField.title?.includes(")");
    const hasCorrectUnit = paramField.title?.includes(`(${correctUnit})`);
    const needsTitleUpdate =
      paramField.title && ((hasParentheses && !hasCorrectUnit) || !hasParentheses);

    const needsDefaultUnitUpdate = paramField.defaultUnit !== correctUnit;

    if (needsTitleUpdate) {
      dispatch(
        updateChartByPath({
          id,
          path: `features.chartTypeParam.properties.${type}.title`,
          value: correctedTitle
        })
      );
    }

    if (needsDefaultUnitUpdate) {
      dispatch(
        updateChartByPath({
          id,
          path: `features.chartTypeParam.properties.${type}.defaultUnit`,
          value: correctUnit
        })
      );
    }

    // Also update the units array in Redux if it was missing
    if (!paramField.units && units) {
      dispatch(
        updateChartByPath({
          id,
          path: `features.chartTypeParam.properties.${type}.units`,
          value: units
        })
      );
    }
  }, [
    unitType,
    paramField?.units,
    paramField?.title,
    paramField?.defaultUnit,
    paramField?.property,
    correctedTitle,
    type,
    id,
    dispatch,
    categories
  ]);

  // The box plot ranks by the median of the y field, and its button only renders on the
  // y axis, so correctedTitle is that field's title under the active unit system — the
  // stored chartTypeParam title still carries whichever unit it was saved with.
  const boxPlotSortLabel = `median ${correctedTitle || "y-axis"}`;

  return (
    <Wrapper className={wrapperClassnames}>
      <LogToggle
        className={logToggleClassnames}
        data-testid={`${type}-log-toggle-container`}>
        <span>Log</span>
        <Switch
          size="small"
          data-testid={`${type}-log-toggle`}
          checked={isLogScaleActive}
          onChange={(isActive) => updateLog(isActive)}
        />
      </LogToggle>

      {xAxis && cumProductSections.length > 0 && (
        <ProductSelectContainer>
          <PressureChartOptions sections={cumProductSections} />
        </ProductSelectContainer>
      )}

      <FieldContainer className={fieldContainerClassName} data-testid={`${type}-field`}>
        {(((isStackedBarChart || isTrendDate) && yAxis) ||
          (isStackedBarHorizontal && xAxis)) && (
          <StackedBarCalculation onSelect={handleStackedBarCalculationChange} />
        )}

        {isSplitYearStructurallyPossible && (
          <HideableControl $hidden={!isSplitYearAvailable}>
            <Tooltip
              placement={buttonTooltipPlacement}
              title={
                splitYearFieldMismatch
                  ? "Combine by year is on, but this field isn't set up for it — please contact support"
                  : splitYearNoData
                  ? "Combine by year is on, but no quarter/month buckets were found in the current data"
                  : "Combine by year"
              }>
              <SplitYearButton
                size="small"
                $on={splitYearActive}
                $warning={splitYearNoData || splitYearFieldMismatch}
                onClick={() => {
                  dispatch(
                    updateChartByPath({
                      id,
                      path: "features.splitYear.active",
                      value: !splitYearActive
                    })
                  );
                }}
                icon={<Icon path={mdiCalendarRange} size={1.2} />}
                data-testid={"split-year-button"}
                data-warning={splitYearNoData || splitYearFieldMismatch}
              />
            </Tooltip>
          </HideableControl>
        )}

        {/* swapAxesParamFields only swaps field content between the x/y
            slots — axis roles stay fixed per chart type — so it's safe
            regardless of chart type. Stays mounted (hidden, not unmounted)
            while histogram is on so the other controls don't reflow. */}
        <HideableControl $hidden={isHistogramOn}>
          <Tooltip placement={buttonTooltipPlacement} title="Swap x and y axis field">
            <AxisBinButton
              data-testid={`${type}-swap-axis-button`}
              size="small"
              icon={<SwapVertIcon />}
              onClick={swapAxesParamFields}
              disabled={!isValidAxesSwap}
            />
          </Tooltip>
        </HideableControl>

        {/* Numeric axis always shows the selector; category axis hides it
            while histogram mode is on (auto-driven by group-by focus
            instead). Stays mounted (hidden, not unmounted) so the other
            controls don't reflow. */}
        <HideableControl $hidden={isCategoryAxis && isHistogramOn}>
          <GroupBy
            value={paramField}
            dataTypeFilters={axisDataTypeFilter}
            chartType={chartType}
            onChange={(gb, _a, _b, _c, source, performanceDateRange) => {
              // Only a forecast pick is an explicit choice of SOURCE, so only that marks the
              // axis. The marker is written onto the axis itself rather than held in the
              // component, so it travels with the value: it survives a remount, and the
              // swap-axes button and focus mirror carry it along when they copy the axis
              // object, instead of leaving a stale flag behind on the component.
              updateParamField(
                isForecastQualifiedProperty(gb.property)
                  ? { ...gb, forecastFolderExplicit: true }
                  : gb,
                source,
                performanceDateRange
              );
            }}
            placement="left"
            entityKind={entityKind}
            isDisabled={isFieldPickerDisabled}
            axisType={type}
            chartTypeParam={chartTypeParamState.properties[type]}
            forceClose={isCategoryAxis && isHistogramOn}>
            <StyledButton size="small">
              <span>
                {(((isStackedBarChart || isTrendDate) && yAxis) ||
                  (isStackedBarHorizontal && xAxis)) &&
                isCalcMethodCount
                  ? entityKind === EntityKind.Well
                    ? "Well Count"
                    : "Facility Count"
                  : correctedTitle}
              </span>
              <Icon path={mdiChevronDown} size={0.75} />
            </StyledButton>
          </GroupBy>
        </HideableControl>
        {isCategoryAxis && (
          <HideableControl $hidden={isHistogramOn}>
            <Tooltip
              placement={buttonTooltipPlacement}
              title={`Sync ${categoryAxisKey}-axis field to focus field`}>
              <SyncButton
                size={"small"}
                $on={syncToFocusField}
                onClick={() => {
                  setSyncToFocusField(!syncToFocusField);
                  if (!syncToFocusField) {
                    setSortOptionIndex(0);
                  }
                  dispatch(
                    updateChartByPath({
                      id,
                      path: "features.xAxisFieldSync.active",
                      value: !syncToFocusField
                    })
                  );
                }}
                icon={<Icon path={mdiBullseye} size={1.0} />}
                data-testid={"sync-button"}
              />
            </Tooltip>
          </HideableControl>
        )}
        {(isJitterPlot || isMosaicChart || isStackedBarChart || isStackedBarHorizontal) &&
          isCategoryAxis && (
            <HideableControl
              $hidden={(isStackedBarChart || isStackedBarHorizontal) && isHistogramOn}>
              <Popover
                trigger="click"
                placement="topRight"
                overlayClassName="chart-bin-settings"
                open={axisBinOpen}
                onOpenChange={async (visible) => {
                  // The Tooltip wraps the disabled button in a span that still takes clicks.
                  if (visible && syncToFocusField) return;
                  // The popover shows the axis's own bin, whatever local edits or fetches did.
                  if (visible && paramField.bin) setBin(paramField.bin);
                  if (visible && !paramField.bin && filterId.id && filterId.id !== "") {
                    await refetch();
                  } else if (!visible && !paramField.bin && binRequest?.data) {
                    dispatch(
                      updateChartByPath({
                        id,
                        path: "features.chartTypeParam.properties",
                        value: withCategoryAxisBin(bin)
                      })
                    );
                  }
                  setAxisBinOpen(visible);
                }}
                content={
                  <AxisBinContainer>
                    <BinsSettingsHeader>
                      <StyledText strong>Bin Settings</StyledText>
                    </BinsSettingsHeader>
                    <BinSettings
                      bin={bin}
                      dataType={paramField.dataType}
                      entityKind={entityKind}
                      canEditBin={paramField.canBin}
                      onBinChange={(newBin) => setAxisBin(newBin)}
                      onBinSettingChange={updateAxisBin}
                      onReset={() => resetAxisBin()}
                      isBinningFromAxis={true}
                      linkedAxisLabel={linkedAxisLabel}
                      headerActions={
                        showsMaxBinControls &&
                        hideOthers?.show &&
                        canHideOthers(bin) && (
                          <EvaTooltip title={HIDE_OTHERS_TOOLTIP}>
                            <HideOthersButton
                              type="button"
                              role="switch"
                              aria-label="Hide Others"
                              aria-checked={isHidingOthers}
                              data-testid={`${type}-hide-others-switch`}
                              onClick={() =>
                                dispatch(
                                  updateChartByPath({
                                    id,
                                    path: "features.hideOthers.active",
                                    value: !isHidingOthers
                                  })
                                )
                              }>
                              <Icon
                                path={isHidingOthers ? mdiEyeOff : mdiEye}
                                size="16px"
                              />
                            </HideOthersButton>
                          </EvaTooltip>
                        )
                      }
                    />
                  </AxisBinContainer>
                }>
                <Tooltip
                  placement={buttonTooltipPlacement}
                  title={
                    !syncToFocusField
                      ? "Edit bin settings"
                      : `Unsync ${categoryAxisKey}-axis to edit bins`
                  }>
                  <AxisBinButton
                    data-testid={`${type}-bin-settings-button`}
                    size="small"
                    icon={
                      <AllInboxIcon
                        style={{ transform: `rotate(${categoryIconRotation}deg)` }}
                      />
                    }
                    disabled={syncToFocusField}
                  />
                </Tooltip>
              </Popover>
            </HideableControl>
          )}
        {((isStackedBarChart && xAxis) ||
          (isStackedBarHorizontal && yAxis) ||
          (isBoxPlotChart && yAxis)) && (
          <Tooltip
            placement={buttonTooltipPlacement}
            title={
              isBoxPlotChart && sortOptionIndex !== 0
                ? `${sortOptions[sortOptionIndex].tooltip} by ${boxPlotSortLabel}`
                : sortOptions[sortOptionIndex].tooltip
            }>
            <StackedBarSortButton
              size={"small"}
              $on={sortOptionIndex !== 0}
              onClick={() => {
                if (syncToFocusField && !isBoxPlotChart) {
                  setSyncToFocusField(!syncToFocusField);
                  dispatch(
                    updateChartByPath({
                      id,
                      path: "features.xAxisFieldSync.active",
                      value: !syncToFocusField
                    })
                  );
                }
                cycleSortOption();
              }}
              icon={
                <Icon
                  path={sortOptions[sortOptionIndex].icon}
                  size={1.2}
                  rotate={sortIconRotation}
                />
              }
              data-testid={"xaxis-sort-button"}
            />
          </Tooltip>
        )}
      </FieldContainer>
    </Wrapper>
  );
}

export default ChartAxisFieldSelector;

const Wrapper = styled.div`
  ${visibilityToggleStyles};
  position: absolute;
  left: 0;
  bottom: 0;
  display: flex;

  &.xAxis {
    right: 0;
    height: 24px;
    z-index: 0.5;
  }

  &.yAxis {
    top: 0;
    z-index: 1;

    &.yLogPositionAdjusted {
      left: unset;
      right: 0;
    }
  }

  &.yAxis.stackDown {
    z-index: auto;
  }
`;

const LogToggle = styled.div`
  ${visibilityToggleStyles};
  position: absolute;
  display: flex;
  align-items: center;
  gap: 3px;
  color: var(--color-text);
  font-weight: 500;
  font-size: 12px;
  z-index: 1;

  &.xAxis {
    right: 44px;
    bottom: 4px;

    &.xLogPositionAdjusted,
    &.noBottomXAxis {
      right: 4px;
    }
  }

  &.yAxis {
    bottom: -12px;
    transform-origin: left top;
    transform: translateX(4px) rotate(-90deg);

    &.yLogPositionAdjusted {
      transform: translateX(-22px) rotate(-90deg);
      bottom: 16px !important;
    }

    &.noBottomXAxis {
      bottom: 35px;
    }
  }
`;

const FieldContainer = styled.div`
  ${visibilityToggleStyles};
  position: absolute;
  display: flex;
  align-items: center;

  &.stackUp {
    z-index: 1;
  }

  &.xAxis {
    bottom: 0;
    left: 50%;
    transform: translateX(calc(24px - 50%));
  }

  &.yAxis {
    right: 0;
    top: 50%;
    transform: translate(calc(50% + 14px), -50%) rotate(-90deg);
    transform-origin: center;
  }
`;

// Keeps a control mounted (space reserved) while hidden, instead of
// unmounting it — so toggling it doesn't shift every other control on the axis.
const HideableControl = styled.span<{ $hidden?: boolean }>`
  display: inline-flex;
  align-items: center;
  visibility: ${(props) => (props.$hidden ? "hidden" : "visible")};
`;

const ProductSelectContainer = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  z-index: 2;

  &.stackUp {
    z-index: 1;
  }

  bottom: 0;
  left: 50%;
  transform: translateX(calc(24px - 50%));
`;

const StyledButton = styled(Button)`
  max-width: 180px;
  width: 180px;

  & > span {
    width: 100%;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding-right: 4px;

  & > span {
    flex: 1;
    min-width: 0;
  }
`;

// Spaced like the focus panel's lock button sits from Reset.
const HideOthersButton = styled(BinIconButton)`
  margin-right: 9px;
`;

const AxisBinContainer = styled.div`
  max-width: 36rem;
  background-color: var(--color-text-06);
  display: grid;
  gap: 1px;
`;

const AxisBinButton = styled(Button)`
  color: var(--color-text);
  display: grid;
  justify-content: center;
  align-items: center;
`;
const StyledText = styled(Text)`
  text-transform: uppercase;
  letter-spacing: 0.25px;
`;

const BinsSettingsHeader = styled.div`
  height: 40px;
  display: flex;
  align-items: center;
  background-color: #fff;
  padding: 0 var(--space-4);
`;

const SyncButton = styled(AxisBinButton)`
  background-color: ${(props: { $on?: boolean }) =>
    props.$on ? "var(--color-primary)" : "white"};
  color: ${(props: { $on?: boolean }) => (props.$on ? "white" : "#a2aaad")};

  &:hover {
    background-color: ${(props: { $on?: boolean }) =>
      props.$on ? "var(--color-primary)" : "white"};
    color: ${(props: { $on?: boolean }) => (props.$on ? "white" : "#a2aaad")};
  }

  &:focus {
    background-color: ${(props: { $on?: boolean }) =>
      props.$on ? "var(--color-primary)" : "white"};
    color: ${(props: { $on?: boolean }) => (props.$on ? "white" : "#a2aaad")};
  }

  &:active {
    background-color: ${(props: { $on?: boolean }) =>
      props.$on ? "var(--color-primary)" : "white"};
    color: ${(props: { $on?: boolean }) => (props.$on ? "white" : "#a2aaad")};
  }
`;

const getSplitYearButtonBackground = (props: { $on?: boolean; $warning?: boolean }) =>
  props.$warning ? "var(--color-danger)" : props.$on ? "var(--color-primary)" : "white";
const getSplitYearButtonColor = (props: { $on?: boolean; $warning?: boolean }) =>
  props.$warning || props.$on ? "white" : "#a2aaad";

const SplitYearButton = styled(AxisBinButton)`
  background-color: ${getSplitYearButtonBackground};
  color: ${getSplitYearButtonColor};

  &:hover {
    background-color: ${getSplitYearButtonBackground};
    color: ${getSplitYearButtonColor};
  }

  &:focus {
    background-color: ${getSplitYearButtonBackground};
    color: ${getSplitYearButtonColor};
  }

  &:active {
    background-color: ${getSplitYearButtonBackground};
    color: ${getSplitYearButtonColor};
  }
`;

const StackedBarSortButton = styled(AxisBinButton)`
  background-color: ${(props: { $on?: boolean }) =>
    props.$on ? "var(--color-primary)" : "white"};
  color: ${(props: { $on?: boolean }) => (props.$on ? "white" : "#a2aaad")};

  &:hover {
    background-color: ${(props: { $on?: boolean }) =>
      props.$on ? "var(--color-primary)" : "white"};
    color: ${(props: { $on?: boolean }) => (props.$on ? "white" : "#a2aaad")};
  }

  &:focus {
    background-color: ${(props: { $on?: boolean }) =>
      props.$on ? "var(--color-primary)" : "white"};
    color: ${(props: { $on?: boolean }) => (props.$on ? "white" : "#a2aaad")};
  }

  &:active {
    background-color: ${(props: { $on?: boolean }) =>
      props.$on ? "var(--color-primary)" : "white"};
    color: ${(props: { $on?: boolean }) => (props.$on ? "white" : "#a2aaad")};
  }
`;
